import { Directive, HostBinding, Input, isDevMode } from '@angular/core';
import { environment } from '../../../../../environments/environment';

/**
 * Renders a stable `data-testid` hook for the Playwright E2E suite.
 *
 * `data-testid` is Playwright's zero-config default (`getByTestId` -> `[data-testid]`) and our
 * documented preferred selector, but only a handful of templates use it directly. This directive
 * centralizes the attribute so specs select through intentional, resilient hooks instead of
 * brittle tag/CSS/text selectors. It mirrors the existing v2 shared-directive idiom
 * (standalone + `@HostBinding('attr...')`, e.g. `ClickableIconDirective`).
 *
 * Positioned as the resilient fallback to role/label locators — reach for `e2eId` on icon-only
 * buttons, non-semantic containers, table rows, and dynamic content, not as a blanket replacement
 * for `getByRole`. See `tests/e2e/STYLE-GUIDE.md` for the selector ordering.
 *
 * @example
 * <button mat-flat-button e2eId="transactions-pay-button" (click)="save()">Save</button>
 * // spec:
 * await page.getByTestId('transactions-pay-button').click();
 */
@Directive({
    selector: '[e2eId]',
    standalone: true,
})
export class E2eIdDirective {
    /**
     * Required. Flat kebab-case `{component}-{element}` hook, e.g. `transactions-pay-button`.
     * Keep it static — never interpolate a dynamic record id into the value (use a separate
     * `data-id` attribute for that); dynamic ids in a testid make specs brittle.
     */
    @Input({ required: true }) e2eId!: string;

    /**
     * Namespace seam. Empty today (flat kebab per SD-8475). If value collisions ever appear, a
     * later escalation to a `component__element` scheme can prefix values here without touching
     * a single call site.
     */
    private static readonly NAMESPACE_PREFIX = '';

    /** Kebab-case shape: lowercase letters/digits, single hyphens (matches the STYLE-GUIDE convention). */
    private static readonly KEBAB_CASE = /^[a-z][a-z0-9]*(-[a-z0-9]+)*$/;

    /** The last value we warned about — the getter runs every change-detection cycle, so warn once per value. */
    private warnedValue: string | null = null;

    @HostBinding('attr.data-testid')
    get testId(): string | null {
        // Off-by-default escape hatch: render no attribute when hooks are stripped.
        if (environment.stripE2eHooks) {
            return null;
        }

        if (isDevMode()) {
            this.warnIfNotKebabCase(this.e2eId);
        }

        return `${E2eIdDirective.NAMESPACE_PREFIX}${this.e2eId}`;
    }

    private warnIfNotKebabCase(value: string): void {
        // Getter fires on every CD pass; only warn when the value is new (avoids console spam).
        if (value === this.warnedValue) {
            return;
        }

        if (!E2eIdDirective.KEBAB_CASE.test(value)) {
            this.warnedValue = value;
            console.warn(
                    `[e2eId] value "${value}" is not flat kebab-case ({component}-{element}). ` +
                    'Use lowercase letters, digits, and single hyphens, e.g. "transactions-pay-button".',
            );
        }
    }
}
